import React, {
  ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementType,
  forwardRef,
  type ReactElement,
  Ref,
} from 'react';
import { Button as MuiButton } from '@mui/material';

import { SHAPE_CIRCULAR, SHAPE_SQUARE } from '@/mui/constants';

import type { ShapeProp } from '@/mui/types';

type HTMLTagMap = {
  div: HTMLDivElement;
  span: HTMLSpanElement;
  a: HTMLAnchorElement;
  button: HTMLButtonElement;
};

type MuiProps = ComponentProps<typeof MuiButton>;

type PolymorphicRef<C extends ElementType> = C extends keyof HTMLTagMap ? Ref<HTMLTagMap[C]> : Ref<unknown>;

export type Props<C extends ElementType> = {
  component?: C;
  shape?: ShapeProp;
  square?: boolean;
} & MuiProps &
  Omit<ComponentPropsWithoutRef<C>, keyof MuiProps | 'component'>;

function PolymorphicComponentImpl<C extends ElementType = 'button'>(
  props: Props<C>,
  ref: PolymorphicRef<C>,
): ReactElement {
  const { shape, square, sx, ...rest } = props;

  return (
    <MuiButton
      {...rest}
      // TypeScript can't infer polymorphic ref on MUI base, safe to cast
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as Ref<any>}
      sx={{
        ...(shape === SHAPE_SQUARE && {
          borderRadius: 0,
        }),
        ...(shape === SHAPE_CIRCULAR && {
          borderRadius: (theme) => theme.shape.borderRadiusCircular,
        }),
        ...(square === true && {
          aspectRatio: '1 / 1',
          minWidth: 'auto',
          padding: 0,
        }),
        ...sx,
      }}
    />
  );
}

const PolymorphicComponent = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  PolymorphicComponentImpl as unknown as (props: Props<any>, ref: PolymorphicRef<any>) => ReactElement,
);

const Button = PolymorphicComponent as <C extends ElementType = 'button'>(
  props: Props<C> & { ref?: PolymorphicRef<C> },
) => ReactElement | null;

// @ts-expect-error TS can't detect displayName from forwardRef
Button.displayName = 'Button';

export default Button;
